import axios, { AxiosRequestConfig, CancelTokenSource } from 'axios'
import _ from 'lodash'
import values from 'lodash/values'
import { computed, makeObservable } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { Model } from 'modules/core/lib/mobx-rest'

import Filter from 'modules/reports/store/models/Filter'
import type { ProjectionParams } from 'modules/reports/store/models/Projection'
import Projection from 'modules/reports/store/models/Projection'
import type { GenerateParams, ReportParams } from 'modules/reports/store/models/Report'

import type { ConditionParams } from './Condition'
import type { IResource, ResourceData } from './Resource'

class Template extends Model<IResource> {
  static CHART_TYPES = {
    bars: 'bars',
    pie: 'pie',
    lines: 'lines',
    area: 'area',
    table: 'table',
    numeric_metric: 'numeric_metric',
  }

  constructor(attr: IResource = {}) {
    super(attr)
    makeObservable(this, {
      title: computed,
      slug: computed,
      type: computed,
      chartSelected: computed,
      projections: computed,
      filters: computed,
      sqlQuery: computed,
      sqlConfiguration: computed,
    })
  }

  static urlRoot() {
    return '/api/rest/reports/template'
  }

  get title(): string {
    return this.attributes.title
  }

  get slug(): string {
    return this.attributes.slug
  }

  get type(): string {
    return this.attributes.type
  }

  get chartSelected(): boolean {
    return values(Template.CHART_TYPES).includes(this.type)
  }

  async generate(data: GenerateParams, cancelToken: CancelTokenSource | null) {
    const params: AxiosRequestConfig = {
      method: 'POST',
      url: buildUrl(`${Template.staticUrl()}/generate`),
      cancelToken: axios.CancelToken.source().token,
      withCredentials: true,
      data,
    }

    if (cancelToken) {
      params['cancelToken'] = cancelToken.token
    }

    const response = await axios(params)

    const lastUpdatedHeader = response.data.last_update

    const parsedResponse: ResourceData = {
      lastUpdated: lastUpdatedHeader && new Date(Date.parse(response.data.last_update)),
      engine: response.data.engine,
      rows: response.data.rows,
      status: response.data.status,
    }
    return parsedResponse
  }

  async order(data: GenerateParams) {
    const { value } = await asyncAction(() => (this.collection || this).rpc('order', data), {
      successFlash: false,
    })

    return value
  }

  get projections(): Projection[] {
    if (_.isNil(this.attributes.projections)) return []

    return this.attributes.projections.map(
      (projection: ProjectionParams) => new Projection(projection)
    )
  }

  get filters(): Filter[] {
    if (_.isNil(this.attributes.filters)) return []

    return this.attributes.filters.map((filter: ConditionParams[]) => Filter.buildFromArray(filter))
  }

  get sqlQuery(): string | undefined | null {
    return this.attributes.sql_query
  }

  get sqlConfiguration(): Record<string, string | undefined | null> | undefined | null {
    return this.attributes.sql_configuration
  }

  serialize(): ReportParams {
    return {
      id: this.attributes.id,
      title: this.attributes.title,
      type: this.attributes.chart_type,
    }
  }
}

export default Template
